import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Permission, RoleDefinition } from '@/lib/permissions';

export function usePermissions() {
  const { user } = useAuth();

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_user_permissions', {
        _user_id: user!.id,
      });
      if (error) throw error;
      return (data || []) as Permission[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const can = (domain: string, action: string): boolean => {
    return permissions.some(p => p.domain === domain && p.action === action);
  };

  const canAny = (domain: string, actions: string[]): boolean => {
    return actions.some(a => can(domain, a));
  };

  const getScope = (domain: string, action: string): string | null => {
    const perm = permissions.find(p => p.domain === domain && p.action === action);
    return perm?.scope ?? null;
  };

  return { permissions, isLoading, can, canAny, getScope };
}

export function useRoleDefinitions() {
  return useQuery({
    queryKey: ['role-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role_definitions')
        .select('*')
        .order('level', { ascending: false });
      if (error) throw error;
      return data as RoleDefinition[];
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useRolePermissions(roleDefinitionId: string | null) {
  return useQuery({
    queryKey: ['role-permissions', roleDefinitionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .eq('role_definition_id', roleDefinitionId!);
      if (error) throw error;
      return data as { id: string; domain: string; action: string; scope: string }[];
    },
    enabled: !!roleDefinitionId,
  });
}

export function useUserOverrides(userId: string | null) {
  return useQuery({
    queryKey: ['user-overrides', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_permission_overrides')
        .select('*')
        .eq('user_id', userId!);
      if (error) throw error;
      return data as { id: string; domain: string; action: string; scope: string; granted: boolean }[];
    },
    enabled: !!userId,
  });
}
